import * as parser from "cron-parser";

import { OperationMode, Stream } from "@/apollo/types";

import { SourceStream } from "./types";

/** What "full sync at midnight" always meant, now that it is only a schedule by another name. */
export const MIDNIGHT_CRON = "0 0 * * *";

/**
 * Folds the deprecated `fullSyncAtMidnight` into the schedule that replaced it. Reading it here
 * means the option is gone from the UI everywhere at once, and writing the pair back turns the
 * next save of the sync into the migration.
 */
export function fullSyncScheduleFrom(stream: {
  fullSyncAtMidnight?: boolean | null;
  fullSyncSchedule?: string | null;
}): string | null {
  return (
    stream.fullSyncSchedule ??
    (stream.fullSyncAtMidnight ? MIDNIGHT_CRON : null)
  );
}

/** Shortest gap the expression ever produces, in minutes; undefined when it will not parse. */
function frequencyInMinutes(cron: string): number | undefined {
  try {
    const expression = parser.parseExpression(cron);
    let previous = expression.next();
    let shortest = Number.MAX_VALUE;
    for (let i = 0; i < 10; i++) {
      const next = expression.next();
      shortest = Math.min(shortest, next.getTime() - previous.getTime());
      previous = next;
    }
    return shortest / 60_000;
  } catch {
    return undefined;
  }
}

/**
 * A scheduled full sync is the table's next ordinary run promoted to FULL, so one that fires more
 * often than the table syncs simply makes every run a full sync - worth saying, not worth blocking.
 */
export function fullSyncScheduleOutpacesSync(
  fullSyncSchedule: string,
  syncInterval?: string | null,
): boolean {
  if (!syncInterval) {
    return false;
  }
  const scheduleFrequency = frequencyInMinutes(fullSyncSchedule);
  const syncFrequency = frequencyInMinutes(syncInterval);
  if (scheduleFrequency === undefined || syncFrequency === undefined) {
    return false;
  }
  return scheduleFrequency < syncFrequency;
}

const FULL_SYNC_PERIODS = [
  { minutes: 10, cron: "*/10 * * * *", label: "10 min" },
  { minutes: 15, cron: "*/15 * * * *", label: "15 min" },
  { minutes: 30, cron: "*/30 * * * *", label: "30 min" },
  { minutes: 60, cron: "0 * * * *", label: "hourly" },
  { minutes: 120, cron: "0 */2 * * *", label: "2 hours" },
  { minutes: 180, cron: "0 */3 * * *", label: "3 hours" },
  { minutes: 360, cron: "0 */6 * * *", label: "6 hours" },
  { minutes: 720, cron: "0 */12 * * *", label: "12 hours" },
  { minutes: 1440, cron: "0 0 * * *", label: "daily" },
  { minutes: 2880, cron: "0 0 */2 * *", label: "2 days" },
  { minutes: 4320, cron: "0 0 */3 * *", label: "3 days" },
  { minutes: 10080, cron: "0 0 * * 0", label: "weekly" },
];
const MONTHLY_OPTION = { label: "Every month", value: "0 0 1 * *" };
/** The shortest month, so monthly still lands on every 2nd sync or later. */
const MONTHLY_MIN_GAP_IN_MINUTES = 28 * 24 * 60;
const ORDINAL_SUFFIXES: Record<number, string> = { 1: "st", 2: "nd", 3: "rd" };
const MAX_SYNC_MULTIPLES = 7;

function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) {
    return `${n}th`;
  }
  return `${n}${ORDINAL_SUFFIXES[n % 10] ?? "th"}`;
}

/**
 * Schedules that land on every 2nd, 3rd, ... sync of the table, so none of them outpaces it.
 * Empty when the table's cadence is unknown or too slow to have any.
 */
export function fullSyncScheduleOptions(
  syncInterval?: string | null,
): { label: string; value: string }[] {
  const syncFrequency = syncInterval
    ? frequencyInMinutes(syncInterval)
    : undefined;
  if (!syncFrequency) {
    return [];
  }
  const multiples = FULL_SYNC_PERIODS.filter(
    ({ minutes }) => minutes > syncFrequency && minutes % syncFrequency === 0,
  )
    .slice(0, MAX_SYNC_MULTIPLES)
    .map(({ minutes, cron, label }) => ({
      label: `Every ${ordinal(minutes / syncFrequency)} sync (${label})`,
      value: cron,
    }));
  return syncFrequency * 2 <= MONTHLY_MIN_GAP_IN_MINUTES
    ? [...multiples, MONTHLY_OPTION]
    : multiples;
}

type StreamCapabilities = Pick<
  Stream,
  "supportedModes" | "configurableIncremental"
>;

type FullSyncSettings = Pick<
  SourceStream,
  "fullSyncAlways" | "fullSyncSchedule" | "incrementalPointerId"
>;

/**
 * Whether every run of the stream is already a full sync, so nothing needs scheduling. Mirrors
 * `assertFullSyncScheduled` on the backend, including why the pointer is only read where the
 * customer configures it: elsewhere the stream picks its mode per run.
 */
function runsFullSyncOnEveryRun(
  stream: StreamCapabilities,
  streamState: FullSyncSettings,
): boolean {
  if (
    stream.supportedModes.length === 1 &&
    stream.supportedModes[0] === OperationMode.Full
  ) {
    return true;
  }
  if (streamState.fullSyncAlways) {
    return true;
  }
  return stream.configurableIncremental && !streamState.incrementalPointerId;
}

/** Capturing deletes is only reconciled on a full sync, so the stream needs to reach one. */
export function hasFullSyncToReconcileAgainst(
  stream: StreamCapabilities,
  streamState: FullSyncSettings,
): boolean {
  return (
    runsFullSyncOnEveryRun(stream, streamState) ||
    !!streamState.fullSyncSchedule
  );
}
